<script lang="ts">
  // Home hero, live 2026 only: "Polls close in 1d 23h 59m 59s" ticking every second, then "Polls closed at 7:00 PM ET —
  // waiting for the first results" until votes are counted (then the parent removes it). Fixed-width tabular digits
  // and a fixed card height, so neither the ticking nor the switch at 7:00 moves anything.
  import { countdownParts, fmtCloseTime, fmtTime } from '../../lib/format';
  let { phase, close, now }: { phase: 'countdown' | 'closed'; close: number; now: number } = $props();
  const p = $derived(countdownParts(close - now));
  const segs = $derived([
    { v: String(p.d), k: p.d === 1 ? 'day' : 'days' },
    { v: String(p.h).padStart(2, '0'), k: p.h === 1 ? 'hour' : 'hours' },
    { v: String(p.m).padStart(2, '0'), k: 'min' },
    { v: String(p.s).padStart(2, '0'), k: 'sec' },
  ]);
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  const spoken = $derived(`Polls close in ${p.d ? `${plural(p.d, 'day')}, ` : ''}${plural(p.h, 'hour')}, ${plural(p.m, 'minute')}`);
</script>

<section class="card cd" class:closed={phase === 'closed'} aria-label={phase === 'countdown' ? 'Countdown to polls closing' : 'Polls are closed'}>
  {#if phase === 'countdown'}
    <div class="kick"><svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="13" r="8" fill="none" stroke="currentColor" stroke-width="2.4" /><path d="M12 9v4.2l2.6 1.6M9.5 2.5h5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" /></svg>Polls close in</div>
    <div class="clock" role="timer" aria-label={spoken}>
      {#each segs as s}<div class="seg"><span class="v num">{s.v}</span><span class="k">{s.k}</span></div>{/each}
    </div>
    <div class="when">{fmtCloseTime(close)}</div>
  {:else}
    <div class="kick"><i class="dot" aria-hidden="true"></i>Polls closed</div>
    <p class="msg" role="status">Polls closed at {fmtTime(close)} — waiting for the first results</p>
    <div class="when">The first results usually arrive within the hour. This page updates by itself.</div>
  {/if}
</section>

<style>
  /* one fixed height for both states: the switch at 7:00 PM doesn't move the page */
  .cd { display: flex; flex-direction: column; justify-content: space-between; gap: 10px; height: 172px; padding: 18px 20px; flex: none; width: 380px; max-width: 100%; }
  .kick { display: flex; align-items: center; gap: 7px; font: 700 0.74rem/1 var(--font); letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-2); }
  .clock { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
  .seg { display: flex; flex-direction: column; align-items: center; gap: 4px; background: var(--card-3); border-radius: 12px; padding: 9px 4px 8px; min-width: 0; }
  .v { font: 700 2.2rem/1 var(--display); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; color: var(--ink); }
  .k { font: 600 0.7rem/1 var(--font); letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-3); }
  .when { font-size: 0.86rem; color: var(--ink-2); font-weight: 600; }
  .msg { margin: 0; font: 700 1.3rem/1.25 var(--display); color: var(--ink); }
  .closed .when { font-weight: 400; color: var(--ink-3); }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--good); position: relative; }
  .dot::after { content: ''; position: absolute; inset: 0; border-radius: 50%; background: var(--good); animation: ping 2s ease-out infinite; }
  @keyframes ping { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.6); opacity: 0; } }
  @media (prefers-reduced-motion: reduce) { .dot::after { animation: none; } }
  @media (max-width: 900px) { .cd { width: 100%; } }
  @media (max-width: 400px) { .cd { padding: 16px; height: 164px; } .v { font-size: 1.95rem; } .seg { padding: 8px 2px 7px; } .msg { font-size: 1.18rem; } }
</style>
